import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { apiWorker } from '../../api/worker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';
import { 
  Building2, 
  ShoppingBag, 
  Banknote, 
  Coins, 
  RotateCcw, 
  Sparkles, 
  ArrowRight,
  Briefcase,
  AlertTriangle,
  Award,
  HelpCircle,
  Trophy,
  X,
  Heart,
  Shield,
  Zap,
  TrendingDown,
  TrendingUp,
  Sliders
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Sound FX (Web Audio API Synthesizer)
// ---------------------------------------------------------------------------

const playChiptune = (type: 'buy' | 'sell' | 'travel' | 'encounter' | 'damage' | 'win' | 'heal') => {
  if (typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;

    if (type === 'buy') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(800, now + 0.08);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (type === 'sell') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
      gain.gain.setValueAtTime(0.12, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
    } else if (type === 'travel') {
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(300, now);
      osc.frequency.linearRampToValueAtTime(500, now + 0.15);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    } else if (type === 'encounter') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.setValueAtTime(300, now + 0.08);
      osc.frequency.setValueAtTime(150, now + 0.16);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.24);
      osc.start(now);
      osc.stop(now + 0.24);
    } else if (type === 'damage') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(60, now + 0.2);
      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (type === 'win') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(523.25, now); // C5
      osc.frequency.setValueAtTime(659.25, now + 0.08); // E5
      osc.frequency.setValueAtTime(783.99, now + 0.16); // G5
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
    } else if (type === 'heal') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    }
  } catch {
    // Ignore audio context autoplay blocks
  }
};

// ---------------------------------------------------------------------------
// Types & Game Config
// ---------------------------------------------------------------------------

export interface Commodity {
  id: string;
  name: string;
  category: string;
  icon: string;
  baseMinPrice: number;
  baseMaxPrice: number;
  description: string;
}

export interface District {
  id: string;
  name: string;
  tagline: string;
  riskFactor: number;
}

export interface DefenseGear {
  id: string;
  name: string;
  defenseBonus: number;
  cost: number;
  icon: string;
  description: string;
}

export interface EncounterEvent {
  id: string;
  title: string;
  description: string;
  type: 'cops' | 'muggers' | 'shady_vendor' | 'loan_shark' | 'flash_sale';
  data?: any;
}

export interface LeaderboardEntry {
  playerName: string;
  score?: number;
  netWorth: number;
  date?: string;
  metadata?: Record<string, any>;
}

const COMMODITIES: Commodity[] = [
  { id: 'flyers', name: 'Event Flyers', category: 'Print', icon: '📄', baseMinPrice: 5, baseMaxPrice: 25, description: 'High demand near campus rush week.' },
  { id: 'tshirts', name: 'Custom Merch T-Shirts', category: 'Apparel', icon: '👕', baseMinPrice: 80, baseMaxPrice: 250, description: 'Staple commodity for all chapter events.' },
  { id: 'banners', name: 'Vinyl Stage Banners', category: 'Signage', icon: '🏁', baseMinPrice: 300, baseMaxPrice: 900, description: 'Heavy and expensive, but huge profit margins.' },
  { id: 'coffee', name: 'Artisanal Coffee Bags', category: 'Catering', icon: '☕', baseMinPrice: 15, baseMaxPrice: 65, description: 'Keeps student leaders awake during board meetings.' },
  { id: 'lanyards', name: 'VIP Lanyards & Badges', category: 'Supplies', icon: '🏷️', baseMinPrice: 35, baseMaxPrice: 140, description: 'Essential for conference registration desks.' },
  { id: 'prizes', name: 'Raffle Gift Baskets', category: 'Fundraising', icon: '🎁', baseMinPrice: 500, baseMaxPrice: 2200, description: 'Luxury items subject to volatile market spikes!' }
];

const DISTRICTS: District[] = [
  { id: 'hq', name: 'National HQ District', tagline: 'Strict governance, high security, stable prices.', riskFactor: 0.15 },
  { id: 'campus', name: 'University Quad', tagline: 'Volatile student demand, frequent flash sales.', riskFactor: 0.25 },
  { id: 'downtown', name: 'Metro Convention Center', tagline: 'High volume trading, expensive storage.', riskFactor: 0.20 },
  { id: 'techpark', name: 'Innovation Hub', tagline: 'High tech sponsors, massive price swings.', riskFactor: 0.35 },
  { id: 'suburbs', name: 'Regional Chapter House', tagline: 'Quiet suburban market with steady demand.', riskFactor: 0.10 }
];

const GEAR_CATALOG: DefenseGear[] = [
  { id: 'megaphone', name: 'High-Decibel Megaphone', defenseBonus: 10, cost: 300, icon: '📢', description: 'Rallies crowds and slows down campus security raids (+10 Def).' },
  { id: 'campus_pass', name: 'VIP Campus Access Badge', defenseBonus: 25, cost: 800, icon: '🪪', description: 'Grants immunity to minor administrative fines (+25 Def).' },
  { id: 'legal_retainer', name: 'General Counsel Retainer', defenseBonus: 50, cost: 2500, icon: '⚖️', description: 'Top-tier legal defense against HQ audits and muggers (+50 Def).' }
];

const TOTAL_DAYS = 30;
const INITIAL_CASH = 1000;
const INITIAL_DEBT = 2000;
const INITIAL_CAPACITY = 100;
const INITIAL_HEALTH = 100;
const DEBT_INTEREST_RATE = 0.08;
const VAULT_INTEREST_RATE = 0.03;

const getSavedState = () => {
  if (typeof window === 'undefined') return null;
  try {
    const saved = localStorage.getItem('org_market_wars_save');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Keep Math.random() out of the component render scope (event-handler closures are
// linted as render-time); these module-level helpers are opaque to the purity rule.
const randomUnit = () => Math.random();
const rollChance = (p: number) => Math.random() < p;
const randomIndex = (n: number) => Math.floor(Math.random() * n);
const randomInt = (min: number, max: number) => Math.floor(Math.random() * max) + min;

export function OrgMarketWars() {
  const queryClient = useQueryClient();

  const savedState = getSavedState();

  const [day, setDay] = useState<number>(savedState?.day || 1);
  const [currentDistrict, setCurrentDistrict] = useState<District>(() => {
    if (savedState?.districtId) {
      return DISTRICTS.find((d) => d.id === savedState.districtId) || DISTRICTS[0];
    }
    return DISTRICTS[0];
  });
  const [cash, setCash] = useState<number>(savedState?.cash ?? INITIAL_CASH);
  const [bank, setBank] = useState<number>(savedState?.bank ?? 0);
  const [debt, setDebt] = useState<number>(savedState?.debt ?? INITIAL_DEBT);
  const [health, setHealth] = useState<number>(savedState?.health ?? INITIAL_HEALTH);
  const [maxHealth] = useState<number>(INITIAL_HEALTH);
  const [storageCapacity, setStorageCapacity] = useState<number>(savedState?.storageCapacity ?? INITIAL_CAPACITY);
  const [inventory, setInventory] = useState<Record<string, number>>(
    savedState?.inventory || { flyers: 0, tshirts: 0, banners: 0, coffee: 0, lanyards: 0, prizes: 0 }
  );
  const [ownedGear, setOwnedGear] = useState<string[]>(savedState?.ownedGear || []);
  const [capacityCost, setCapacityCost] = useState<number>(savedState?.capacityCost ?? 500);

  // Market Prices & Events
  const [marketPrices, setMarketPrices] = useState<Record<string, number>>({});
  const [marketEvents, setMarketEvents] = useState<string[]>([]);
  const [log, setLog] = useState<string[]>(['Welcome to Org Market Wars! Out-trade rivals and survive 30 days!']);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [gameOverReason, setGameOverReason] = useState<string>('');

  // UI Theme & Modals
  const [retroTheme, setRetroTheme] = useState<boolean>(savedState?.retroTheme ?? false);
  const [showHowToPlay, setShowHowToPlay] = useState<boolean>(false);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [activeEncounter, setActiveEncounter] = useState<EncounterEvent | null>(null);

  // Bank & Debt Modal Input
  const [bankAmountInput, setBankAmountInput] = useState<string>('');
  const [debtAmountInput, setDebtAmountInput] = useState<string>('');
  
  // Score Submission
  const { isSignedIn, user } = useCurrentUser();
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [playerName, setPlayerName] = useState<string>(() => user?.username || user?.fullName || user?.firstName || '');
  const [scoreSubmitted, setScoreSubmitted] = useState<boolean>(false);

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  // Leaderboard Query
  const { data: leaderboardsRaw, isLoading: isLeaderboardLoading } = useQuery<any>({
    queryKey: ['org-market-wars-high-scores', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try {
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/tools/org-market-wars/high-scores', { params, silent: true });
        return res.data;
      } catch (err) {
        console.warn('Failed to fetch org-market-wars high scores from server; falling back to local records:', err);
        return [];
      }
    }
  });

  const remoteLeaderboards: LeaderboardEntry[] = (
    Array.isArray(leaderboardsRaw)
      ? leaderboardsRaw
      : Array.isArray(leaderboardsRaw?.content)
        ? leaderboardsRaw.content
        : Array.isArray(leaderboardsRaw?.data)
          ? leaderboardsRaw.data
          : []
  ).map((item: any) => ({
    playerName: item?.playerName || 'Unknown',
    netWorth: item?.netWorth ?? item?.score ?? 0,
    score: item?.score ?? item?.netWorth ?? 0,
    date: item?.date || new Date().toISOString().split('T')[0],
    metadata: item?.metadata
  }));

  const leaderboards: LeaderboardEntry[] = (() => {
    if (remoteLeaderboards.length > 0) {
      return remoteLeaderboards.sort((a: any, b: any) => (b.netWorth || b.score) - (a.netWorth || a.score));
    }
    try {
      const saved = localStorage.getItem('org_market_wars_scores');
      const parsed = saved ? JSON.parse(saved) : [];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((item: any) => ({
          playerName: item?.playerName || 'Unknown',
          netWorth: item?.netWorth ?? item?.score ?? 0,
          score: item?.score ?? item?.netWorth ?? 0,
          date: item?.date || new Date().toISOString().split('T')[0],
          metadata: item?.metadata
        })).sort((a: any, b: any) => (b.netWorth || b.score) - (a.netWorth || a.score));
      }
    } catch {
      // Ignore local storage error
    }
    return [];
  })();

  const currentInventoryCount = Object.values(inventory).reduce((a, b) => a + b, 0);

  const totalDefenseBonus = ownedGear.reduce((acc, gearId) => {
    const item = GEAR_CATALOG.find((g) => g.id === gearId);
    return acc + (item ? item.defenseBonus : 0);
  }, 0);

  // Auto-save active game session state
  useEffect(() => {
    if (!gameOver) {
      const saveData = {
        day,
        districtId: currentDistrict.id,
        cash,
        bank,
        debt,
        health,
        storageCapacity,
        inventory,
        ownedGear,
        capacityCost,
        retroTheme
      };
      localStorage.setItem('org_market_wars_save', JSON.stringify(saveData));
    } else {
      localStorage.removeItem('org_market_wars_save');
    }
  }, [day, currentDistrict, cash, bank, debt, health, storageCapacity, inventory, ownedGear, capacityCost, retroTheme, gameOver]);

  // High score backend save mutation
  const submitScoreMutation = useMutation({
    mutationFn: async (scoreData: { playerName: string; netWorth: number }) => {
      return await apiWorker.post('/api/tools/org-market-wars/high-scores', {
        playerName: scoreData.playerName,
        score: scoreData.netWorth,
        game: 'org-market-wars',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          netWorth: scoreData.netWorth
        }
      }, { silent: true });
    },
    onSuccess: (res: any, variables) => {
      setScoreSubmitted(true);
      try {
        const saved = localStorage.getItem('org_market_wars_scores');
        const list = saved ? JSON.parse(saved) : [];
        const updated = Array.isArray(list) ? list : [];
        updated.push({
          playerName: variables.playerName,
          score: variables.netWorth,
          netWorth: variables.netWorth,
          date: new Date().toISOString().split('T')[0]
        });
        localStorage.setItem('org_market_wars_scores', JSON.stringify(updated));
      } catch {
        // Ignore local storage failure
      }
      const isNewHigh = res?.data?.isNewHighScore;
      toast.success(isNewHigh ? '🏆 New High Score Recorded!' : 'High score recorded successfully!');
      queryClient.invalidateQueries({ queryKey: ['org-market-wars-high-scores'] });
      setShowLeaderboard(true);
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'omw-401' });
      } else {
        toast.error('Could not connect to leaderboard server.');
      }
    }
  });

  const generateMarketPrices = (district: District) => {
    const newPrices: Record<string, number> = {};
    const events: string[] = [];

    COMMODITIES.forEach((item) => {
      let min = item.baseMinPrice;
      let max = item.baseMaxPrice;

      const roll = Math.random();
      if (roll < 0.08) {
        min = Math.max(1, Math.floor(min * 0.2));
        max = Math.max(5, Math.floor(max * 0.3));
        events.push(`📉 SURPLUS CRASH! ${item.name} flooded the market!`);
      } else if (roll > 0.92) {
        min = Math.floor(min * 3.5);
        max = Math.floor(max * 4.5);
        events.push(`📈 INSANE SHORTAGE! ${item.name} price skyrocketed!`);
      }

      const price = Math.floor(Math.random() * (max - min + 1)) + min;
      newPrices[item.id] = price;
    });

    setMarketPrices(newPrices);
    setMarketEvents(events);
  };

  useEffect(() => {
    const saved = localStorage.getItem('org_market_wars_save');
    if (saved) {
      const distId = JSON.parse(saved).districtId;
      const loadedDistrict = DISTRICTS.find((d) => d.id === distId) || DISTRICTS[0];
      queueMicrotask(() => generateMarketPrices(loadedDistrict));
    } else {
      queueMicrotask(() => generateMarketPrices(DISTRICTS[0]));
    }
  }, []);

  const handleBuy = (item: Commodity, quantity: number = 1) => {
    const price = marketPrices[item.id] || item.baseMinPrice;
    const totalCost = price * quantity;

    if (cash < totalCost) {
      toast.error('Insufficient cash funds!');
      return;
    }
    if (currentInventoryCount + quantity > storageCapacity) {
      toast.error('Not enough storage capacity!');
      return;
    }

    setCash((prev) => prev - totalCost);
    setInventory((prev) => ({
      ...prev,
      [item.id]: (prev[item.id] || 0) + quantity
    }));
    playChiptune('buy');
    addLog(`Bought ${quantity}x ${item.name} for $${totalCost.toLocaleString()}.`);
  };

  const handleSell = (item: Commodity, quantity: number = 1) => {
    const currentQty = inventory[item.id] || 0;
    if (currentQty < quantity) {
      toast.error(`You do not own ${quantity}x ${item.name}!`);
      return;
    }

    const price = marketPrices[item.id] || item.baseMinPrice;
    const totalRevenue = price * quantity;

    setCash((prev) => prev + totalRevenue);
    setInventory((prev) => ({
      ...prev,
      [item.id]: prev[item.id] - quantity
    }));
    playChiptune('sell');
    addLog(`Sold ${quantity}x ${item.name} for $${totalRevenue.toLocaleString()}.`);
  };

  const triggerRandomEncounter = (district: District): EncounterEvent | null => {
    // Check loan shark debt raid
    if (debt > 5000 && rollChance(0.45)) {
      return {
        id: 'loan_shark_raid',
        title: '🚨 LOAN SHARK ENFORCERS AMBUSH!',
        description: `Your debt reached $${debt.toLocaleString()}! Regional Treasurer enforcers corner you in an alley demanding immediate repayment!`,
        type: 'loan_shark',
        data: { fine: Math.floor(cash * 0.4) + 200, damage: 30 }
      };
    }

    const roll = randomUnit();
    if (roll < district.riskFactor * 1.1) {
      const subType = randomUnit();
      if (subType < 0.4) {
        const bribeAmount = Math.max(100, Math.floor(cash * 0.25));
        return {
          id: 'campus_cops',
          title: '👮 CAMPUS SECURITY AUDIT RAID!',
          description: 'Campus Officers spot your unauthorized pop-up chapter store! They demand a permit fine or threaten confiscation!',
          type: 'cops',
          data: { bribe: bribeAmount, damage: 25 }
        };
      } else if (subType < 0.7) {
        const mugAmount = Math.max(50, Math.floor(cash * 0.3));
        return {
          id: 'rival_muggers',
          title: '🦹 RIVAL CHAPTER CUTTHROATS AMBUSH!',
          description: 'A gang of rogue chapter reps attempt to mug your cash supply!',
          type: 'muggers',
          data: { mugAmount, damage: 35 }
        };
      } else if (subType < 0.85) {
        return {
          id: 'shady_vendor',
          title: '🎒 SHADY ALUMNUS TRUNK DEAL!',
          description: 'An alumnus opens his car trunk: "Hey kid, need +40 extra inventory storage slots for $350?"',
          type: 'shady_vendor',
          data: { cost: 350, capacityGain: 40 }
        };
      } else {
        const randomItem = COMMODITIES[randomIndex(COMMODITIES.length)];
        const dealQty = randomInt(10, 15);
        const discountPrice = Math.max(2, Math.floor((marketPrices[randomItem.id] || randomItem.baseMinPrice) * 0.25));
        return {
          id: 'flash_sale',
          title: `⚡ LIQUIDATION DISPOSAL: ${randomItem.name.toUpperCase()}!`,
          description: `A graduating senior dumps ${dealQty}x ${randomItem.name} for just $${discountPrice} each!`,
          type: 'flash_sale',
          data: { item: randomItem, qty: dealQty, price: discountPrice }
        };
      }
    }
    return null;
  };

  const handleTravel = (targetDistrict: District) => {
    if (targetDistrict.id === currentDistrict.id) return;

    playChiptune('travel');

    // 1. Vault Interest (+3%)
    if (bank > 0) {
      const interestGained = Math.floor(bank * VAULT_INTEREST_RATE);
      setBank((prev) => prev + interestGained);
    }

    // 2. Debt Interest (+8%)
    if (debt > 0) {
      const debtInterest = Math.floor(debt * DEBT_INTEREST_RATE);
      setDebt((prev) => prev + debtInterest);
    }

    const nextDay = day + 1;
    setDay(nextDay);
    setCurrentDistrict(targetDistrict);
    generateMarketPrices(targetDistrict);
    addLog(`Traveled to ${targetDistrict.name}. (Day ${nextDay})`);

    // Check random encounters
    const encounter = triggerRandomEncounter(targetDistrict);
    if (encounter) {
      playChiptune('encounter');
      setActiveEncounter(encounter);
    }
  };

  // ---------------------------------------------------------------------------
  // Encounter Resolution Choices
  // ---------------------------------------------------------------------------

  const resolveEncounter = () => {
    setActiveEncounter(null);
  };

  const handleEncounterRun = () => {
    if (!activeEncounter) return;
    const runSuccessChance = 0.50 + (totalDefenseBonus * 0.005);
    const success = rollChance(runSuccessChance);

    if (success) {
      playChiptune('win');
      toast.success('Escaped safely without losing anything!');
      addLog(`Escaped encounter with ${activeEncounter.title}!`);
    } else {
      playChiptune('damage');
      const dmg = activeEncounter.data?.damage || 20;
      const newHp = Math.max(0, health - dmg);
      setHealth(newHp);
      toast.error(`Failed to escape! Took ${dmg} damage!`);
      addLog(`Failed escape! Took ${dmg} damage. HP: ${newHp}/${maxHealth}`);

      if (newHp <= 0) {
        endGame('Expelled & hospitalized from encounter injury!');
      }
    }
    resolveEncounter();
  };

  const handleEncounterFight = () => {
    if (!activeEncounter) return;
    const winChance = 0.40 + (totalDefenseBonus * 0.008);
    const win = rollChance(winChance);

    if (win) {
      playChiptune('win');
      const reward = randomInt(150, 300);
      setCash((prev) => prev + reward);
      toast.success(`VICTORY! Defeated attackers and looted $${reward}!`);
      addLog(`Won fight against ${activeEncounter.title}! Looted $${reward}.`);
    } else {
      playChiptune('damage');
      const dmg = activeEncounter.data?.damage || 30;
      const newHp = Math.max(0, health - dmg);
      setHealth(newHp);
      toast.error(`Lost the fight! Took ${dmg} damage!`);
      addLog(`Lost fight! Took ${dmg} damage. HP: ${newHp}/${maxHealth}`);

      if (newHp <= 0) {
        endGame('Expelled & hospitalized from fight injuries!');
      }
    }
    resolveEncounter();
  };

  const handleEncounterBribe = () => {
    if (!activeEncounter) return;
    const fine = activeEncounter.data?.bribe || activeEncounter.data?.fine || 150;
    if (cash < fine) {
      toast.error('Not enough cash to pay fine! You must run or fight!');
      return;
    }
    setCash((prev) => Math.max(0, prev - fine));
    toast.success(`Paid $${fine.toLocaleString()} fine peacefully.`);
    addLog(`Paid $${fine.toLocaleString()} fine to resolve ${activeEncounter.title}.`);
    resolveEncounter();
  };

  const handleEncounterBuyTrunk = () => {
    if (!activeEncounter || activeEncounter.type !== 'shady_vendor') return;
    const cost = activeEncounter.data.cost;
    if (cash < cost) {
      toast.error('Cannot afford trunk deal!');
      return;
    }
    setCash((prev) => prev - cost);
    setStorageCapacity((prev) => prev + activeEncounter.data.capacityGain);
    playChiptune('buy');
    toast.success(`Bought trunk upgrade (+${activeEncounter.data.capacityGain} capacity)!`);
    addLog(`Bought +${activeEncounter.data.capacityGain} storage slots from trunk vendor.`);
    resolveEncounter();
  };

  const handleEncounterBuyFlashSale = () => {
    if (!activeEncounter || activeEncounter.type !== 'flash_sale') return;
    const { item, qty, price } = activeEncounter.data;
    const totalCost = price * qty;
    if (cash < totalCost) {
      toast.error('Insufficient cash for flash sale!');
      return;
    }
    if (currentInventoryCount + qty > storageCapacity) {
      toast.error('Not enough storage capacity for bulk deal!');
      return;
    }
    setCash((prev) => prev - totalCost);
    setInventory((prev) => ({
      ...prev,
      [item.id]: (prev[item.id] || 0) + qty
    }));
    playChiptune('buy');
    toast.success(`Bought ${qty}x ${item.name} at bargain deal!`);
    addLog(`Liquidation deal: bought ${qty}x ${item.name} for $${totalCost.toLocaleString()}.`);
    resolveEncounter();
  };

  // ---------------------------------------------------------------------------
  // Banking & Healthcare Operations
  // ---------------------------------------------------------------------------

  const handleBankDeposit = (amount: number) => {
    if (amount <= 0 || amount > cash) {
      toast.error('Invalid deposit amount.');
      return;
    }
    setCash((prev) => prev - amount);
    setBank((prev) => prev + amount);
    addLog(`Deposited $${amount.toLocaleString()} into Chapter Vault.`);
    setBankAmountInput('');
  };

  const handleBankWithdraw = (amount: number) => {
    if (amount <= 0 || amount > bank) {
      toast.error('Invalid withdrawal amount.');
      return;
    }
    setBank((prev) => prev - amount);
    setCash((prev) => prev + amount);
    addLog(`Withdrew $${amount.toLocaleString()} from Chapter Vault.`);
    setBankAmountInput('');
  };

  const handlePayDebt = (amount: number) => {
    if (amount <= 0 || amount > cash) {
      toast.error('Invalid payment amount.');
      return;
    }
    const payAmount = Math.min(amount, debt);
    setCash((prev) => prev - payAmount);
    setDebt((prev) => prev - payAmount);
    addLog(`Repaid $${payAmount.toLocaleString()} of debt.`);
    setDebtAmountInput('');
  };

  const handleTakeLoan = (amount: number) => {
    if (amount <= 0) return;
    setDebt((prev) => prev + amount);
    setCash((prev) => prev + amount);
    addLog(`Took a $${amount.toLocaleString()} loan from Loan Shark.`);
    setDebtAmountInput('');
  };

  const handleExpandCapacity = () => {
    if (cash < capacityCost) {
      toast.error(`Expanding storage costs $${capacityCost.toLocaleString()}.`);
      return;
    }
    setCash((prev) => prev - capacityCost);
    setStorageCapacity((prev) => prev + 50);
    setCapacityCost((prev) => Math.floor(prev * 1.5));
    addLog(`Expanded inventory capacity by +50 slots.`);
  };

  const handleHealAtInfirmary = () => {
    const cost = 150;
    if (health >= maxHealth) {
      toast.error('Health is already full!');
      return;
    }
    if (cash < cost) {
      toast.error(`Infirmary medical care costs $${cost}.`);
      return;
    }
    setCash((prev) => prev - cost);
    setHealth((prev) => Math.min(maxHealth, prev + 35));
    playChiptune('heal');
    toast.success('Restored +35 HP at Student Infirmary!');
    addLog(`Healed +35 HP at Student Health Center ($150).`);
  };

  const handleBuyGear = (gear: DefenseGear) => {
    if (ownedGear.includes(gear.id)) return;
    if (cash < gear.cost) {
      toast.error(`Cannot afford ${gear.name}!`);
      return;
    }
    setCash((prev) => prev - gear.cost);
    setOwnedGear((prev) => [...prev, gear.id]);
    playChiptune('buy');
    toast.success(`Equipped ${gear.name} (+${gear.defenseBonus} Def)!`);
    addLog(`Purchased ${gear.name} (+${gear.defenseBonus} Defense).`);
  };

  const addLog = (msg: string) => {
    setLog((prev) => [msg, ...prev]);
  };

  const endGame = (reason: string = 'Game Over!') => {
    setGameOverReason(reason);
    setGameOver(true);
    try {
      const currentNetWorth = calculateNetWorth();
      const customSaved = localStorage.getItem('org_market_wars_scores');
      const parsed = customSaved ? JSON.parse(customSaved) : [];
      const list = Array.isArray(parsed) ? parsed : [];
      const name = playerName.trim() || (isSignedIn ? (user?.username || user?.fullName || 'ChapterLeader') : 'Guest');
      list.push({
        playerName: name,
        score: currentNetWorth,
        netWorth: currentNetWorth,
        date: new Date().toISOString().split('T')[0]
      });
      list.sort((a: any, b: any) => (b.netWorth || b.score) - (a.netWorth || a.score));
      localStorage.setItem('org_market_wars_scores', JSON.stringify(list.slice(0, 20)));
    } catch {
      // Best-effort leaderboard save; ignore storage failures.
    }
  };

  const calculateNetWorth = () => {
    let inventoryVal = 0;
    COMMODITIES.forEach((item) => {
      const qty = inventory[item.id] || 0;
      inventoryVal += qty * (marketPrices[item.id] || item.baseMinPrice);
    });
    return cash + bank + inventoryVal - debt;
  };

  const resetGame = () => {
    setDay(1);
    setCurrentDistrict(DISTRICTS[0]);
    setCash(INITIAL_CASH);
    setBank(0);
    setDebt(INITIAL_DEBT);
    setHealth(INITIAL_HEALTH);
    setStorageCapacity(INITIAL_CAPACITY);
    setInventory({ flyers: 0, tshirts: 0, banners: 0, coffee: 0, lanyards: 0, prizes: 0 });
    setOwnedGear([]);
    setGameOver(false);
    setScoreSubmitted(false);
    setCapacityCost(500);
    setActiveEncounter(null);
    generateMarketPrices(DISTRICTS[0]);
    setLog(['Game restarted. Good luck surviving DrugWars market dynamics!']);
  };

  const netWorth = calculateNetWorth();

  const getPriceBadge = (item: Commodity) => {
    const price = marketPrices[item.id] || item.baseMinPrice;
    const avgPrice = Math.floor((item.baseMinPrice + item.baseMaxPrice) / 2);
    const diffPct = Math.round(((price - avgPrice) / avgPrice) * 100);

    if (diffPct <= -40) {
      return (
        <span className="bg-emerald-500 text-white text-[10px] font-mono font-black px-1.5 py-0.5 rounded-none flex items-center gap-0.5">
          <TrendingDown className="w-3 h-3" /> BARGAIN ({diffPct}%)
        </span>
      );
    } else if (diffPct >= 80) {
      return (
        <span className="bg-rose-600 text-white text-[10px] font-mono font-black px-1.5 py-0.5 rounded-none flex items-center gap-0.5">
          <TrendingUp className="w-3 h-3" /> SPIKE (+{diffPct}%)
        </span>
      );
    }
    return (
      <span className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-none">
        NORMAL
      </span>
    );
  };

  return (
    <div className={`w-full max-w-6xl mx-auto p-4 sm:p-6 font-sans transition-colors ${
      retroTheme ? 'bg-[#9bbc0f] text-[#0f380f] border-8 border-[#0f380f]' : 'text-black dark:text-white'
    }`}>
      {/* Top Header Banner */}
      <div className={`p-6 border-4 shadow-[6px_6px_0px_#000000] mb-6 rounded-none relative overflow-hidden ${
        retroTheme ? 'bg-[#8bac0f] border-[#0f380f] text-[#0f380f]' : 'bg-[#FFD600] border-black text-black dark:border-white'
      }`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-mono font-bold uppercase tracking-widest px-3 py-1 rounded-none ${
                retroTheme ? 'bg-[#0f380f] text-[#9bbc0f]' : 'bg-black text-[#FFD600]'
              }`}>
                {retroTheme ? 'TI-83 RETRO CALCULATOR ENGINE' : 'BAUHAUS TRADING ENGINE'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tighter flex items-center gap-3">
              <Building2 className="w-10 h-10 text-[#0044FF]" />
              Org Market Wars
            </h1>
            <p className="font-semibold text-sm mt-1 max-w-xl">
              Authentic DrugWars dynamic trading! Buy low, sell high, survive security raids, fight cutthroats, and maximize Net Worth!
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setRetroTheme(!retroTheme)}
              className={`font-black uppercase tracking-wider text-xs py-3 px-4 border-2 shadow-[3px_3px_0px_#000000] flex items-center gap-1.5 rounded-none transition-transform ${
                retroTheme ? 'bg-[#0f380f] text-[#9bbc0f] border-[#0f380f]' : 'bg-purple-700 text-white border-black'
              }`}
            >
              <Sliders className="w-4 h-4" />
              {retroTheme ? 'Standard Theme' : 'TI-83 Retro Mode'}
            </button>
            <button
              onClick={() => setShowLeaderboard(true)}
              className="bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-wider text-xs py-3 px-4 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] flex items-center gap-1.5 rounded-none"
            >
              <Trophy className="w-4 h-4 text-[#FFD600]" />
              Hall of Fame
            </button>
            <button
              onClick={() => setShowHowToPlay(true)}
              className="bg-white hover:bg-slate-100 text-black font-black uppercase tracking-wider text-xs py-3 px-4 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] flex items-center gap-1.5 rounded-none"
            >
              <HelpCircle className="w-4 h-4 text-[#0044FF]" />
              Rules
            </button>
            <button
              onClick={resetGame}
              className="bg-black hover:bg-slate-800 text-white font-black uppercase tracking-wider text-xs py-3 px-4 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000000] flex items-center gap-1.5 rounded-none"
            >
              <RotateCcw className="w-4 h-4 text-[#FFD600]" />
              Restart
            </button>
          </div>
        </div>
      </div>

      {/* Interactive Travel Encounter Modal */}
      <AnimatePresence>
        {activeEncounter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-black border-4 border-black dark:border-white max-w-xl w-full p-6 shadow-[8px_8px_0px_#FF0000] space-y-4 text-left font-sans text-black dark:text-white"
            >
              <div className="flex items-center gap-3 border-b-4 border-black dark:border-white pb-3">
                <AlertTriangle className="w-8 h-8 text-rose-600 animate-pulse" />
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tight text-rose-600 font-mono">
                    {activeEncounter.title}
                  </h3>
                  <p className="text-xs font-mono font-bold text-slate-500">DISTRICT TRAVEL INTERCEPT</p>
                </div>
              </div>

              <p className="text-base font-medium leading-relaxed bg-slate-100 dark:bg-slate-900 p-4 border-2 border-black dark:border-white font-mono">
                {activeEncounter.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {(activeEncounter.type === 'cops' || activeEncounter.type === 'muggers' || activeEncounter.type === 'loan_shark') && (
                  <>
                    <button
                      onClick={handleEncounterRun}
                      className="bg-amber-500 hover:bg-amber-600 text-black font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Run Away (Speed)
                    </button>
                    <button
                      onClick={handleEncounterFight}
                      className="bg-rose-600 hover:bg-rose-700 text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center gap-2"
                    >
                      <Shield className="w-5 h-5" />
                      Fight (Def: +{totalDefenseBonus})
                    </button>
                  </>
                )}

                {(activeEncounter.type === 'cops' || activeEncounter.type === 'loan_shark') && (
                  <button
                    onClick={handleEncounterBribe}
                    className="sm:col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                  >
                    Pay Fine / Bribe (${(activeEncounter.data?.bribe || activeEncounter.data?.fine || 150).toLocaleString()})
                  </button>
                )}

                {activeEncounter.type === 'shady_vendor' && (
                  <>
                    <button
                      onClick={handleEncounterBuyTrunk}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                    >
                      Buy Capacity Upgrade (${activeEncounter.data.cost})
                    </button>
                    <button
                      onClick={() => resolveEncounter()}
                      className="bg-slate-300 dark:bg-slate-800 text-black dark:text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                    >
                      Decline & Move On
                    </button>
                  </>
                )}

                {activeEncounter.type === 'flash_sale' && (
                  <>
                    <button
                      onClick={handleEncounterBuyFlashSale}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                    >
                      Buy Bulk Disposal (${(activeEncounter.data.price * activeEncounter.data.qty).toLocaleString()})
                    </button>
                    <button
                      onClick={() => resolveEncounter()}
                      className="bg-slate-300 dark:bg-slate-800 text-black dark:text-white font-black uppercase py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                    >
                      Ignore Deal
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* How To Play Modal */}
      <AnimatePresence>
        {showHowToPlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-black border-4 border-black dark:border-white max-w-xl w-full p-6 shadow-[8px_8px_0px_#0044FF] space-y-4 rounded-none text-left"
            >
              <div className="flex justify-between items-center border-b-4 border-black dark:border-white pb-3">
                <h3 className="text-2xl font-black uppercase tracking-tight flex items-center gap-2 text-black dark:text-white font-mono">
                  <Briefcase className="w-6 h-6 text-[#0044FF]" />
                  DrugWars Rules & Mechanics
                </h3>
                <button
                  onClick={() => setShowHowToPlay(false)}
                  className="bg-black text-white dark:bg-white dark:text-black font-black p-1 hover:bg-[#0044FF] hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-3 text-sm font-sans text-black dark:text-white">
                <div className="bg-[#FFD600] text-black p-3 border-2 border-black font-bold">
                  🎯 GOAL: Maximize Net Worth while keeping HP above 0!
                </div>
                <div className="space-y-2 font-medium">
                  <p><strong>1. Health System (100 HP):</strong> Taking too much damage in encounters leads to expulsion! Heal at the Student Infirmary for $150.</p>
                  <p><strong>2. Random Encounters:</strong> Traveling between districts triggers Campus Security raids, rival chapter muggers, or secret trunk sales.</p>
                  <p><strong>3. Defense Gear:</strong> Purchase Megaphones, Campus Passes, or Legal Retainers to boost combat and escape odds.</p>
                  <p><strong>4. Chapter Vault Savings:</strong> Vault deposits compound +3% interest daily on travel!</p>
                </div>
              </div>

              <div className="pt-3 border-t-4 border-black dark:border-white text-right">
                <button
                  onClick={() => setShowHowToPlay(false)}
                  className="w-full bg-[#0044FF] text-white font-black uppercase py-3 border-2 border-black hover:bg-blue-700 rounded-none shadow-[3px_3px_0px_#000]"
                >
                  Got It, Let's Trade!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leaderboard Modal */}
      <AnimatePresence>
        {showLeaderboard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-black border-4 border-black dark:border-white max-w-xl w-full p-6 shadow-[8px_8px_0px_#FFD600] space-y-4 rounded-none text-left"
            >
              <div className="flex justify-between items-center border-b-4 border-black dark:border-white pb-3">
                <h3 className="text-2xl font-black uppercase tracking-tight flex items-center gap-2 text-black dark:text-white">
                  <Trophy className="w-7 h-7 text-[#FFD600]" />
                  Hall of Fame Leaderboard
                </h3>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="bg-black text-white dark:bg-white dark:text-black font-black p-1 hover:bg-[#FFD600] hover:text-black"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {hasActiveNode && (
                <GameLeaderboardScopeTabs
                  scope={leaderboardScope}
                  onScopeChange={setLeaderboardScope}
                  nodeName={nodeName}
                />
              )}

              {isLeaderboardLoading ? (
                <div className="p-8 text-center font-mono font-bold">Loading Top Scores...</div>
              ) : leaderboards.length === 0 ? (
                <div className="p-8 text-center font-mono text-slate-500">No high scores registered yet. Start trading to set a record!</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto font-mono">
                  {leaderboards.map((entry, idx) => (
                    <div 
                      key={idx} 
                      className={`flex justify-between items-center p-3 border-2 border-black dark:border-white ${
                        idx === 0 ? 'bg-[#FFD600] text-black font-black' : idx === 1 ? 'bg-slate-200 text-black font-bold' : idx === 2 ? 'bg-amber-100 text-black font-bold' : 'bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 text-center font-black text-lg">#{idx + 1}</span>
                        <div>
                          <div className="font-black text-sm uppercase">{entry.playerName}</div>
                          <div className="text-[10px] opacity-75">{entry.date}</div>
                        </div>
                      </div>
                      <div className="text-lg font-black text-right">
                        ${entry.netWorth.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-3 border-t-4 border-black dark:border-white text-right">
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="w-full bg-black text-white dark:bg-white dark:text-black font-black uppercase py-3 border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white rounded-none"
                >
                  Close Hall of Fame
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Over Screen Overlay */}
      <AnimatePresence>
        {gameOver && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-black border-4 border-black dark:border-white rounded-none p-8 shadow-[8px_8px_0px_#FF0000] mb-8 text-center max-w-2xl mx-auto space-y-4"
          >
            <div className="w-16 h-16 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center mx-auto">
              <Award className="w-10 h-10 text-black" />
            </div>
            <h2 className="text-4xl font-black uppercase tracking-tighter text-black dark:text-white">
              Game Over!
            </h2>
            <p className="text-rose-600 font-mono font-bold text-sm">
              {gameOverReason}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4 font-mono">
              <div className="bg-slate-100 dark:bg-slate-900 p-3 border-2 border-black dark:border-white">
                <div className="text-xs text-slate-500 uppercase">Cash</div>
                <div className="text-lg font-black text-emerald-600 dark:text-emerald-400">${cash.toLocaleString()}</div>
              </div>
              <div className="bg-slate-100 dark:bg-slate-900 p-3 border-2 border-black dark:border-white">
                <div className="text-xs text-slate-500 uppercase">Vault</div>
                <div className="text-lg font-black text-blue-600 dark:text-blue-400">${bank.toLocaleString()}</div>
              </div>
              <div className="bg-slate-100 dark:bg-slate-900 p-3 border-2 border-black dark:border-white">
                <div className="text-xs text-slate-500 uppercase">Debt</div>
                <div className="text-lg font-black text-rose-600 dark:text-rose-400">${debt.toLocaleString()}</div>
              </div>
              <div className="bg-slate-100 dark:bg-slate-900 p-3 border-2 border-black dark:border-white">
                <div className="text-xs text-slate-500 uppercase">Net Worth</div>
                <div className={`text-lg font-black ${netWorth >= 0 ? 'text-[#0044FF]' : 'text-rose-600'}`}>
                  ${netWorth.toLocaleString()}
                </div>
              </div>
            </div>

            {isSignedIn ? (
              !scoreSubmitted ? (
                <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-4">
                  <input 
                    type="text"
                    placeholder="Enter Chapter Leader Name..."
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="flex-1 bg-white dark:bg-slate-900 border-2 border-black dark:border-white rounded-none px-4 py-2 text-sm font-mono focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      if (!playerName.trim()) {
                        toast.error('Please enter your name.');
                        return;
                      }
                      submitScoreMutation.mutate({ playerName: playerName.trim(), netWorth });
                    }}
                    disabled={submitScoreMutation.isPending}
                    className="bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase px-6 py-2 border-2 border-black dark:border-white rounded-none"
                  >
                    {submitScoreMutation.isPending ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              ) : (
                <p className="text-emerald-600 font-black text-sm mb-4 uppercase">✓ Score recorded on global hall of fame!</p>
              )
            ) : (
              <div className="p-4 bg-slate-100 dark:bg-slate-900 border-2 border-black dark:border-white max-w-md mx-auto mb-4 text-center">
                <p className="text-xs text-slate-500 font-mono mb-1">Score saved locally on this device.</p>
                <p className="text-sm font-black uppercase tracking-tight text-black dark:text-white mb-2">Sign in to save your score to the global leaderboard.</p>
                <Link
                  to="/sign-in"
                  className="inline-block bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase px-6 py-2.5 border-2 border-black dark:border-white rounded-none text-xs tracking-wider transition-colors"
                >
                  Sign In to Save Score
                </Link>
              </div>
            )}

            <button onClick={resetGame} className="w-full sm:w-auto bg-black text-white dark:bg-white dark:text-black font-black uppercase py-4 px-8 border-2 border-black dark:border-white rounded-none">
              Play Again
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main HUD Stats */}
      <div className="sticky top-[72px] lg:top-[88px] z-40 bg-[#f5f5f0] dark:bg-black py-4 -mx-4 px-4 grid grid-cols-2 md:grid-cols-6 gap-4 mb-6 border-b-4 border-black dark:border-white shadow-[0px_8px_0px_rgba(0,0,0,0.1)]">
        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center justify-between text-slate-500">
            <span>Day</span>
            <span className="text-[#0044FF] font-black">{day}</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-800 h-2.5 border border-black mt-2 overflow-hidden">
            <div className="bg-[#FFD600] h-full transition-all duration-300" style={{ width: `100%` }} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center justify-between text-slate-500">
            <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-600 fill-rose-600" /> Health</span>
            <span className="text-rose-600 font-black">{health}/{maxHealth}</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-800 h-2.5 border border-black mt-2 overflow-hidden">
            <div className="bg-rose-600 h-full transition-all duration-300" style={{ width: `${(health / maxHealth) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1 text-slate-500">
            <Coins className="w-3.5 h-3.5 text-emerald-600" />
            Cash
          </div>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1 font-mono">
            ${cash.toLocaleString()}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1 text-slate-500">
            <Banknote className="w-3.5 h-3.5 text-blue-600" />
            Vault (+3%)
          </div>
          <div className="text-xl font-black text-blue-600 dark:text-blue-400 mt-1 font-mono">
            ${bank.toLocaleString()}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1 text-slate-500">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            Debt (+8%)
          </div>
          <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-1 font-mono">
            ${debt.toLocaleString()}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-3.5 shadow-[4px_4px_0px_#000000]">
          <div className="text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1 text-slate-500">
            <ShoppingBag className="w-3.5 h-3.5 text-amber-500" />
            Storage
          </div>
          <div className="text-xl font-black text-black dark:text-white mt-1 font-mono">
            {currentInventoryCount}/{storageCapacity}
          </div>
        </div>
      </div>

      {/* Main Grid: Trading Desk vs Districts & Financials */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left 2 Cols: Marketplace Trading Desk */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Market News & Events */}
          {marketEvents.length > 0 && (
            <div className="bg-[#FFD600] text-black border-2 border-black p-4 space-y-1 shadow-[4px_4px_0px_#000000]">
              <div className="text-xs font-mono font-black uppercase tracking-widest flex items-center gap-2 text-black">
                <Sparkles className="w-4 h-4" /> Market Ticker & Spikes
              </div>
              {marketEvents.map((evt, idx) => (
                <p key={idx} className="text-sm font-bold text-black font-sans">
                  {evt}
                </p>
              ))}
            </div>
          )}

          {/* Commodity Market List */}
          <div className="bg-white dark:bg-slate-900 border-4 border-black dark:border-white p-5 shadow-[6px_6px_0px_#000000]">
            <div className="flex justify-between items-center mb-4 border-b-2 border-black dark:border-white pb-3">
              <h2 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                <ShoppingBag className="w-6 h-6 text-[#0044FF]" />
                {currentDistrict.name} Commodity Market
              </h2>
              <span className="text-xs text-slate-500 font-mono font-bold">
                Encounter Risk: {Math.round(currentDistrict.riskFactor * 100)}%
              </span>
            </div>

            <div className="space-y-3">
              {COMMODITIES.map((item) => {
                const price = marketPrices[item.id] || item.baseMinPrice;
                const owned = inventory[item.id] || 0;
                const canAffordOne = cash >= price;
                const maxAffordable = Math.min(
                  Math.floor(cash / price),
                  storageCapacity - currentInventoryCount
                );

                return (
                  <div 
                    key={item.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-950 border-2 border-black dark:border-white hover:border-[#0044FF] transition-colors gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-2xl p-2 bg-white dark:bg-black border border-black dark:border-white">
                        {item.icon}
                      </div>
                      <div>
                        <div className="font-black text-black dark:text-white flex items-center gap-2 flex-wrap">
                          {item.name}
                          {getPriceBadge(item)}
                          {owned > 0 && (
                            <span className="bg-[#FFD600] text-black text-xs font-mono font-black px-2 py-0.5 border border-black">
                              Owned: {owned}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-sans">{item.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-2 sm:pt-0 border-black/20 dark:border-white/20">
                      <div className="text-right">
                        <div className="text-xs text-slate-500 font-mono font-bold uppercase">Price</div>
                        <div className="text-xl font-black text-black dark:text-white font-mono">
                          ${price.toLocaleString()}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={gameOver}
                          onClick={() => handleBuy(item, 1)}
                          className={`font-black text-xs px-3 py-1.5 border border-black uppercase ${
                            (!canAffordOne || currentInventoryCount >= storageCapacity)
                              ? 'bg-emerald-800/60 text-white/70 hover:bg-emerald-700'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          }`}
                        >
                          Buy 1
                        </button>
                        {maxAffordable > 1 && (
                          <button
                            disabled={gameOver}
                            onClick={() => handleBuy(item, maxAffordable)}
                            className="bg-white hover:bg-slate-100 text-emerald-700 border border-black font-black text-xs px-2 py-1.5 uppercase font-mono"
                          >
                            Max ({maxAffordable})
                          </button>
                        )}

                        <button
                          disabled={gameOver}
                          onClick={() => handleSell(item, 1)}
                          className={`font-black text-xs px-3 py-1.5 border border-black uppercase ${
                            owned <= 0
                              ? 'bg-rose-900/60 text-white/70 hover:bg-rose-800'
                              : 'bg-rose-600 hover:bg-rose-700 text-white'
                          }`}
                        >
                          Sell 1
                        </button>
                        {owned > 1 && (
                          <button
                            disabled={gameOver}
                            onClick={() => handleSell(item, owned)}
                            className="bg-white hover:bg-slate-100 text-rose-700 border border-black font-black text-xs px-2 py-1.5 uppercase font-mono"
                          >
                            All ({owned})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right 1 Col: Districts & Financial Management */}
        <div className="space-y-6">
          
          {/* Travel District Selection */}
          <div className="bg-white dark:bg-slate-900 border-4 border-black dark:border-white p-5 shadow-[6px_6px_0px_#000000]">
            <h3 className="text-sm font-black uppercase tracking-wider text-black dark:text-white font-mono mb-3 flex items-center justify-between border-b-2 border-black dark:border-white pb-2">
              <span>District Transport</span>
              <span className="text-[#0044FF] text-xs font-black">+1 Day Travel</span>
            </h3>

            <div className="space-y-2">
              {DISTRICTS.map((dist) => {
                const isCurrent = dist.id === currentDistrict.id;
                return (
                  <button
                    key={dist.id}
                    disabled={isCurrent || gameOver}
                    onClick={() => handleTravel(dist)}
                    className={`w-full text-left p-3 border-2 transition-all flex items-center justify-between ${
                      isCurrent
                        ? 'bg-[#FFD600] border-black text-black font-black shadow-[3px_3px_0px_#000]'
                        : 'bg-slate-50 dark:bg-slate-950 border-black dark:border-white hover:border-[#0044FF] text-black dark:text-white'
                    }`}
                  >
                    <div>
                      <div className="text-sm font-black flex items-center gap-1.5 uppercase font-mono">
                        {dist.name}
                        {isCurrent && <span className="text-[10px] font-mono bg-black text-[#FFD600] px-1.5 py-0.2">HERE</span>}
                      </div>
                      <div className="text-xs opacity-80 mt-0.5 font-sans font-medium">{dist.tagline}</div>
                    </div>
                    {!isCurrent && <ArrowRight className="w-4 h-4 text-black dark:text-white" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Defense Gear & Healthcare */}
          <div className="bg-white dark:bg-slate-900 border-4 border-black dark:border-white p-5 shadow-[6px_6px_0px_#000000] space-y-4">
            <h3 className="text-sm font-black uppercase tracking-wider text-black dark:text-white font-mono flex items-center justify-between border-b-2 border-black dark:border-white pb-2">
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#0044FF]" /> Defense & Health</span>
              <span className="text-emerald-600 text-xs font-black">Def: +{totalDefenseBonus}</span>
            </h3>

            {/* Student Infirmary */}
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border-2 border-black dark:border-white flex justify-between items-center">
              <div>
                <div className="text-xs font-black text-black dark:text-white uppercase font-mono flex items-center gap-1">
                  <Heart className="w-3.5 h-3.5 text-rose-600" /> Student Infirmary
                </div>
                <div className="text-xs text-slate-500 font-mono">Restore +35 HP</div>
              </div>
              <button
                disabled={cash < 150 || health >= maxHealth || gameOver}
                onClick={handleHealAtInfirmary}
                className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-black text-xs px-3 py-1.5 border border-black uppercase font-mono"
              >
                $150
              </button>
            </div>

            {/* Defense Gear Catalog */}
            <div className="space-y-2">
              <div className="text-xs font-black font-mono text-slate-500 uppercase">Black Market Defense Shop</div>
              {GEAR_CATALOG.map((gear) => {
                const isOwned = ownedGear.includes(gear.id);
                return (
                  <div key={gear.id} className="p-2.5 bg-slate-50 dark:bg-slate-950 border border-black dark:border-white flex justify-between items-center">
                    <div>
                      <div className="text-xs font-black flex items-center gap-1">
                        <span>{gear.icon}</span> {gear.name}
                      </div>
                      <div className="text-[10px] text-slate-500 font-sans">{gear.description}</div>
                    </div>
                    <button
                      disabled={isOwned || cash < gear.cost || gameOver}
                      onClick={() => handleBuyGear(gear)}
                      className={`font-black text-[10px] px-2.5 py-1 border border-black uppercase font-mono ${
                        isOwned ? 'bg-slate-300 text-slate-600' : 'bg-[#0044FF] text-white hover:bg-blue-700'
                      }`}
                    >
                      {isOwned ? 'OWNED' : `$${gear.cost}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chapter Banking & Debt Management */}
          <div className="bg-white dark:bg-slate-900 border-4 border-black dark:border-white p-5 shadow-[6px_6px_0px_#000000] space-y-4">
            <h3 className="text-sm font-black uppercase tracking-wider text-black dark:text-white font-mono flex items-center gap-2 border-b-2 border-black dark:border-white pb-2">
              <Briefcase className="w-4 h-4 text-[#0044FF]" />
              Chapter Banking & Storage
            </h3>

            {/* Storage Upgrade */}
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border-2 border-black dark:border-white flex justify-between items-center">
              <div>
                <div className="text-xs font-black text-black dark:text-white uppercase font-mono">Expand Storage (+50)</div>
                <div className="text-xs text-slate-500 font-mono">Current: {storageCapacity} slots</div>
              </div>
              <button
                disabled={cash < capacityCost || gameOver}
                onClick={handleExpandCapacity}
                className="bg-[#0044FF] hover:bg-blue-700 disabled:opacity-40 text-white font-black text-xs px-3 py-1.5 border border-black uppercase font-mono"
              >
                ${capacityCost.toLocaleString()}
              </button>
            </div>

            {/* Bank Deposit/Withdraw */}
            <div className="space-y-2 pt-2 border-t-2 border-black dark:border-white">
              <div className="text-xs font-black font-mono text-slate-500 uppercase flex justify-between">
                <span>Vault Balance (${bank.toLocaleString()})</span>
                <span className="text-blue-600 font-black">+3%/day</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Amount..."
                  value={bankAmountInput}
                  onChange={(e) => setBankAmountInput(e.target.value)}
                  className="flex-1 bg-white dark:bg-black border-2 border-black dark:border-white px-3 py-1.5 text-xs font-mono"
                />
                <button
                  disabled={gameOver}
                  onClick={() => handleBankDeposit(Number(bankAmountInput))}
                  className="bg-black text-white dark:bg-white dark:text-black font-black text-xs px-3 py-1 border border-black uppercase"
                >
                  Deposit
                </button>
                <button
                  disabled={gameOver}
                  onClick={() => handleBankWithdraw(Number(bankAmountInput))}
                  className="bg-white text-black dark:bg-slate-800 dark:text-white font-black text-xs px-3 py-1 border border-black uppercase"
                >
                  Withdraw
                </button>
              </div>
            </div>

            {/* Loan Shark / Debt */}
            <div className="space-y-2 pt-2 border-t-2 border-black dark:border-white">
              <div className="text-xs font-black font-mono text-slate-500 uppercase flex justify-between">
                <span>Loan Shark Debt</span>
                <span className="text-rose-600 font-black">+8%/day</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Amount..."
                  value={debtAmountInput}
                  onChange={(e) => setDebtAmountInput(e.target.value)}
                  className="flex-1 bg-white dark:bg-black border-2 border-black dark:border-white px-3 py-1.5 text-xs font-mono"
                />
                <button
                  disabled={gameOver}
                  onClick={() => handlePayDebt(Number(debtAmountInput))}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs px-3 py-1 border border-black uppercase"
                >
                  Pay
                </button>
                <button
                  disabled={gameOver}
                  onClick={() => handleTakeLoan(Number(debtAmountInput))}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-black text-xs px-3 py-1 border border-black uppercase"
                >
                  Borrow
                </button>
              </div>
            </div>
          </div>

          {/* Activity Log Terminal */}
          <div className="bg-black text-[#FFD600] border-4 border-black dark:border-white p-4 font-mono text-xs shadow-[6px_6px_0px_#000000] h-48 overflow-y-auto">
            <div className="text-white font-black mb-2 pb-1 border-b border-white/20 uppercase tracking-widest">
              &gt; EXECUTION_LOG.TXT
            </div>
            {log.map((entry, idx) => (
              <div key={idx} className="leading-relaxed opacity-90 py-0.5">
                &gt; {entry}
              </div>
            ))}
          </div>

        </div>

      </div>
    </div>
  );
}
